// IMPORT/LIBRERIE
import React, { useState, useEffect } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import type { RouteRecord } from "vite-react-ssg";

// CONFIGURAZIONI/COSTANTI
import { RECAPTCHA_SITE_KEY, AGENCY_ID, IUBENDA_SITE_ID, DEFAULT_AGENCY_NAME, appConfig } from "./config/appConfig";
import i18n from "./config/i18n/i18n";

// COMPONENTI
import NotFound from "./components/Error/NotFound";
import HomePage from "./components/Home/Home";
import AboutUs from "./components/AboutUs/AboutUs";
import Contacts from "./components/Contacts/Contacts";
import Services from "./components/Services/Services";
import Training from "./components/AboutUs/Training";
import WorkingMethod from "./components/AboutUs/WorkingMethod";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import Immobili from "./components/Properties/Immobili";
import ImmobiliSuccess from "./components/Properties/ImmobiliSuccess";
import PropertySold from "./components/Property/PropertySold";
//import Sell from "./components/Static/Vendi";
import Blog from "./components/Blog/Blog";
import BlogDetail from "./components/Blog/BlogDetail";
import SellHome from "./components/VendereCasa/VendereCasa";
import Buy from "./components/Static/Cerca";
import Dati from "./components/Policies/Routes/Dati";
import PropertyDetail from "./components/Property/Property";
import IubendaButton from "./components/Iubenda/IubendaButton";
import Loader from "./components/Loaders/Loader";
import LanguageSelector from "./components/Language/LanguageSelector";
import LanguageRouteHandler from "./components/Language/LanguageRouteHandler";
import RecaptchaWrapper from "./components/Wrappers/RecaptchaWrapper";
import PolicyPageRoute from './components/Policies/PolicyPageRoute';
import { getPolicyIdForLang } from "./utils/getPolicyIdForLang";
import WhatsAppButton from "./components/Widgets/WhatsappWidget";
import HolidayPopup from "./components/Popups/HolidayPopup";

// HOOK
import { useGetAgencyDetails, Agency } from "./hooks/useGetAgencyDetails";

// FOGLI DI STILE
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import '@flaticon/flaticon-uicons/css/all/all.css';
import "./App.css";
import ScrollToTop from "./utils/scrollToTop";
import TrailingSlashNormalizer from "./utils/trailingSlashNormalizer";


interface AppProps {
  agencyData: Agency | null;
}

interface AppShellProps extends AppProps {
  children: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ agencyData, children }) => {
  const { i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLanguageChange = (lang: string) => {
    i18nInstance.changeLanguage(lang).then(() => {
      if (appConfig.integrations.multilang) {
        const currentPath = location.pathname;
        const newPath = currentPath.replace(/^\/[a-z]{2}(?=\/|$)/, `/${lang}`);
        if (!/^\/[a-z]{2}/.test(currentPath)) {
          navigate(`/${lang}${currentPath}`);
        } else {
          navigate(newPath, { replace: true });
        }
      }
    });
  };

  const headerAgencyData = {
    agency_name: agencyData?.name ?? appConfig.agency.defaults.name,
    phone1: agencyData?.phone1 ?? appConfig.agency.defaults.phone1,
    mobile: agencyData?.mobile ?? appConfig.agency.defaults.mobile,
    mobile_visible: true,
    international_prefix: appConfig.agency.defaults.intlPrefix,
    whatsapp: true,
    facebook: true,
    instagram: true,
    twitter: true,
    linkedin: true,
    youtube:true,
    pinterest: true,
    cookie_solution_iubenda: true,
  };

  if (!RECAPTCHA_SITE_KEY) {
    console.error("App.tsx: RECAPTCHA_SITE_KEY non è definito!");
    return <div>Errore di configurazione: reCAPTCHA non disponibile.</div>;
  }

  const currentAppLanguage = i18nInstance.language.split('-')[0];
  const buttonPolicyId = getPolicyIdForLang(currentAppLanguage);

  return (
    <div className="App">
      <Header
        agencyData={headerAgencyData}
        languageSelector={
          appConfig.ui.header.showLanguageSelector ? <LanguageSelector handleLanguageChange={handleLanguageChange} /> : <></>
        }
      />

      {children}

      <IubendaButton siteId={IUBENDA_SITE_ID} cookiePolicyId={buttonPolicyId} showBanner={appConfig.integrations.iubenda.showBanner} />

      <Footer />

      {appConfig.ui.showWhatsAppButton && <WhatsAppButton />}
      {appConfig.features.holidayPopup.enabled && <HolidayPopup />}

    </div>
  );
};

const App: React.FC<AppProps> = ({ agencyData }) => {
  const [showAgents] = useState(true);

  return (
    <AppShell agencyData={agencyData}>
      <Routes>

        <Route path="/" element={
          <RecaptchaWrapper>
            <HomePage
              seo={true}
              vetrina2={appConfig.ui.homePage.vetrina2}
              pageType={appConfig.ui.homePage.pageType}
              filters={appConfig.ui.homePage.filters}
              showServices={appConfig.ui.homePage.showServices}
              showLocationSearch={appConfig.ui.homePage.showLocationSearch}
              showFooterBannerVendi={appConfig.ui.homePage.showFooterBannerVendi}
              showPresentationSection={appConfig.ui.homePage.showPresentationSection}
            />
          </RecaptchaWrapper>
        }
        />
        <Route path="/chi-sono" element={<AboutUs agencyId={AGENCY_ID} showAgents={false} />} />
        <Route path="/dati" element={<Dati agencyId={AGENCY_ID} />} />

        <Route path="/immobili/*" element={<Immobili agencyId={AGENCY_ID} />} />
        <Route path="/immobili" element={<Immobili agencyId={AGENCY_ID} />} />

        <Route path="/immobili-successi" element={<ImmobiliSuccess agencyId={AGENCY_ID} />} />
        <Route path="/immobili-successi/:idSlug" element={<PropertySold />} />
        <Route path="/servizi" element={<Services />} />
        <Route path="/formazione" element={<Training agencyId={AGENCY_ID} />} />
        <Route path="/metodo-di-lavoro" element={<WorkingMethod agencyId={AGENCY_ID} />} />
        <Route path="/privacy" element={<PolicyPageRoute policyType="privacy" />} />
        <Route path="/cookies" element={<PolicyPageRoute policyType="cookies" />} />
        <Route path="/cerca" element={
          <RecaptchaWrapper>
            <Buy />
          </RecaptchaWrapper>
        }
        />
        <Route path="/vendi-casa" element={
          <RecaptchaWrapper>
            <SellHome />
          </RecaptchaWrapper>
        }
        />
        <Route path="/blog" element={<Blog agencyId={AGENCY_ID} />} />
        <Route path="/articolo/:articleSlug" element={<BlogDetail agencyId={AGENCY_ID} />} />
        <Route path="/contatti" element={
          <RecaptchaWrapper>
            <Contacts
              contactForm={true}
              widgetPartners={true}
              agencyId={AGENCY_ID}
              showAgents={showAgents}
            />
          </RecaptchaWrapper>
        }
        />

        <Route path="/immobile/:idSlug" element={
          <RecaptchaWrapper>
            <PropertyDetail />
          </RecaptchaWrapper>
        }
        />

        <Route path="*" element={<NotFound message="" />} />

      </Routes>
    </AppShell>
  );
};

//--- Wrapper per I18n e Caricamento Iniziale ---
function useAgencyWithTimeout(timeoutMs = 5000) {
  const result = useGetAgencyDetails(AGENCY_ID);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [timeoutMs]);

  useEffect(() => {
    if (result.error) {
      console.error("Errore caricamento dati agenzia:", result.error);
    }
  }, [result.error]);

  const ready = !result.loading || timedOut;

  return { ...result, ready };
}

function AppWithI18n() {
  const { agency, ready } = useAgencyWithTimeout();
  const loaderAgencyData = { agency_name: agency?.name ?? DEFAULT_AGENCY_NAME };

  return (
    <I18nextProvider i18n={i18n}>
      <ScrollToTop />
      <TrailingSlashNormalizer />
      {!ready ? (
        <Loader agencyData={loaderAgencyData} isLoading={true} />
      ) : (
        <LanguageRouteHandler>
          <App agencyData={agency} />
        </LanguageRouteHandler>
      )}
    </I18nextProvider>
  );
}

function BlogPageWithI18n() {
  const { agency, ready } = useAgencyWithTimeout();
  const loaderAgencyData = { agency_name: agency?.name ?? DEFAULT_AGENCY_NAME };

  return (
    <I18nextProvider i18n={i18n}>
      <ScrollToTop />
      <TrailingSlashNormalizer />
      {!ready ? (
        <Loader agencyData={loaderAgencyData} isLoading={true} />
      ) : (
        <LanguageRouteHandler>
          <AppShell agencyData={agency}>
            <Blog agencyId={AGENCY_ID} />
          </AppShell>
        </LanguageRouteHandler>
      )}
    </I18nextProvider>
  );
}

function BlogDetailWithI18n() {
  const { agency, ready } = useAgencyWithTimeout();
  const loaderAgencyData = { agency_name: agency?.name ?? DEFAULT_AGENCY_NAME };

  return (
    <I18nextProvider i18n={i18n}>
      <ScrollToTop />
      <TrailingSlashNormalizer />
      {!ready ? (
        <Loader agencyData={loaderAgencyData} isLoading={true} />
      ) : (
        <LanguageRouteHandler>
          <AppShell agencyData={agency}>
            <BlogDetail agencyId={AGENCY_ID} />
          </AppShell>
        </LanguageRouteHandler>
      )}
    </I18nextProvider>
  );
}

function PropertyDetailWithI18n() {
  const { agency, ready } = useAgencyWithTimeout();
  const loaderAgencyData = { agency_name: agency?.name ?? DEFAULT_AGENCY_NAME };

  return (
    <I18nextProvider i18n={i18n}>
      <ScrollToTop />
      <TrailingSlashNormalizer />
      {!ready ? (
        <Loader agencyData={loaderAgencyData} isLoading={true} />
      ) : (
        <LanguageRouteHandler>
          <AppShell agencyData={agency}>
            <RecaptchaWrapper>
              <PropertyDetail />
            </RecaptchaWrapper>
          </AppShell>
        </LanguageRouteHandler>
      )}
    </I18nextProvider>
  );
}

// --- SSG Helper Functions ---

const fetchBlogArticleSlugs = async (): Promise<string[]> => {
  const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL as string | undefined;
  if (!apiBaseUrl) {
    console.warn("SSG: VITE_APP_API_BASE_URL non definito, salto pre-render articoli blog.");
    return [];
  }

  try {
    const response = await fetch(`${apiBaseUrl}/blog/articoli`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        agencyId: AGENCY_ID,
        limit: 1000,
        withCount: false,
      }),
    });

    if (!response.ok) {
      console.warn(`SSG: fetch articoli fallita (${response.status}), salto pre-render articoli blog.`);
      return [];
    }

    const payload = await response.json();
    const articles = Array.isArray(payload?.data) ? payload.data : [];

    return articles
      .map((article: { id?: number; seoString?: string | null }) => {
        if (typeof article?.id !== "number") {
          return null;
        }
        return `${article.id}-${article.seoString ?? "articolo"}`;
      })
      .filter((slug: string | null): slug is string => Boolean(slug));
  } catch (error) {
    console.warn("SSG: errore durante il fetch articoli blog, salto pre-render articoli.", error);
    return [];
  }
};

interface PropertyListItem {
  id?: number;
}

interface PropertyDetailResponse {
  propertyId?: number;
  typologyDetails?: { seoString?: string };
  contractDetails?: { contractName?: string };
  indirizzo?: {
    comuneSeoString?: string;
    comuneIt?: string;
    frazioneSeoString?: string;
    quartiereSeoString?: string;
  };
}

const sanitizeSeoPart = (value: string | undefined | null): string => {
  if (!value) return "";
  return value
    .toLowerCase()
    .replace(/\/+/g, "-")
    .replace(/\\+/g, "-")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .trim();
};

const fetchPropertySlugs = async (): Promise<string[]> => {
  const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL as string | undefined;
  if (!apiBaseUrl) {
    console.warn("SSG: VITE_APP_API_BASE_URL non definito, salto pre-render immobili.");
    return [];
  }

  try {
    const listResponse = await fetch(`${apiBaseUrl}/properties`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        agencyId: AGENCY_ID,
        limit: 10000,
        offset: 0,
        withCount: false,
        status: [2, 3, 9, 11],
        filters: {},
      }),
    });

    if (!listResponse.ok) {
      console.warn(`SSG: fetch immobili fallita (${listResponse.status}), salto pre-render immobili.`);
      return [];
    }

    const listPayload = await listResponse.json();
    const properties = Array.isArray(listPayload?.data) ? listPayload.data : [];
    const ids = properties
      .map((property: PropertyListItem) => property?.id)
      .filter((id: number | undefined): id is number => typeof id === "number");

    const slugs: string[] = [];

    for (const propertyId of ids) {
      try {
        const detailResponse = await fetch(`${apiBaseUrl}/property`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            propertyId,
            limit: 1,
            offset: 0,
          }),
        });

        if (!detailResponse.ok) {
          continue;
        }

        const property = (await detailResponse.json()) as PropertyDetailResponse;
        if (!property?.propertyId) {
          continue;
        }

        const propertyTypeSeo = sanitizeSeoPart(property.typologyDetails?.seoString) || "immobile";
        const contractSeo = sanitizeSeoPart(property.contractDetails?.contractName?.toLowerCase()) || "dettaglio";
        const citySeo =
          sanitizeSeoPart(property.indirizzo?.comuneSeoString) ||
          sanitizeSeoPart(property.indirizzo?.comuneIt?.toLowerCase().replace(/\s+/g, "-")) ||
          "";
        const locationSeo =
          sanitizeSeoPart(property.indirizzo?.frazioneSeoString) ||
          sanitizeSeoPart(property.indirizzo?.quartiereSeoString) ||
          "";

        const parts = [String(property.propertyId), propertyTypeSeo, contractSeo, citySeo];
        if (locationSeo) {
          parts.push(locationSeo);
        }

        const fullSlug = parts.filter(Boolean).join("-");

        slugs.push(fullSlug);
      } catch {
        // skip invalid property details and continue
      }
    }

    return Array.from(new Set(slugs));
  } catch (error) {
    console.warn("SSG: errore durante il fetch immobili, salto pre-render immobili.", error);
    return [];
  }
};

export const routes: RouteRecord[] = [
  {
    path: "/",
    Component: AppWithI18n,
  },
  {
    path: "/blog",
    Component: BlogPageWithI18n,
  },
  {
    path: "/articolo/:articleSlug",
    Component: BlogDetailWithI18n,
    getStaticPaths: async () => {
      const slugs = await fetchBlogArticleSlugs();
      return slugs.map((articleSlug) => `articolo/${articleSlug}`);
    },
  },
  {
    path: "/immobile/:idSlug",
    Component: PropertyDetailWithI18n,
    getStaticPaths: async () => {
      const slugs = await fetchPropertySlugs();
      return slugs.map((idSlug) => `immobile/${idSlug}`);
    },
  },
  {
    path: "*",
    Component: AppWithI18n,
  },
];

export default AppWithI18n;
